import type { JSX } from 'react';
import { useSelector } from 'react-redux';

import { ButtonRoundedPrimary } from '@comeon/ui';

import { selectCurrency, selectLimitsModalData, selectLocale } from '~/components/account/ducks';
import { selectKreditzFirstEnabled } from '~/store/feature-toggles/payments-feature-toggles';
import Icon, { ICON_CLOSE } from '~/components/icon';
import { textKeys } from '~/components/limits/constants';
import {
	ButtonContainer,
	CloseButton,
	CloseIconWrapper,
	InformationText,
	LimitContainer,
	LimitList,
	LimitRemainingWrapper,
	OrText,
	SubTitle,
	UploadLinkButton,
	UploadOptionsContainer
} from '~/components/limits/styled';
import LinkList from '~/components/misc/link-list';
import { MainTitle } from '~/components/misc/section-head';
import useTranslations from '~/components/translation/use-translations';
import currencyFormatter from '~/libs/currency-formatter';

interface Props {
	onVerifyWithBank: () => void;
	onUploadDocuments: () => void;
	onClose: () => void;
}

const SpendingLimitContent = ({ onVerifyWithBank, onUploadDocuments, onClose }: Props): JSX.Element => {
	const { translations } = useTranslations(textKeys);

	const currency = useSelector(selectCurrency);
	const locale = useSelector(selectLocale);
	const { meta, remainingAmount = 0 } = useSelector(selectLimitsModalData);
	const kreditzFirstEnabled = useSelector(selectKreditzFirstEnabled);

	return (
		<LimitContainer data-at="spending-screen">
			<MainTitle padding="10px 18px">{translations.MOBILE_SPENDING_LIMIT_REACHED_TITLE}</MainTitle>
			<SubTitle>{translations.MOBILE_SPENDING_LIMIT_REACHED_SUBTITLE}</SubTitle>
			<SubTitle>{translations.MOBILE_SPENDING_LIMIT_REACHED_SUMMARY}</SubTitle>
			<CloseButton onClick={onClose}>
				<CloseIconWrapper>
					<Icon type={ICON_CLOSE} />
				</CloseIconWrapper>
			</CloseButton>

			<LimitList className="link-list">
				<LinkList className="deposit-limit" dataAt="spending-limit-list-item">
					<ul>
						<LinkList
							className="u-flex"
							title={translations.MOBILE_RESPONSIBLE_MONTHLY_LIMIT}
							value={currencyFormatter.format(meta?.limit ?? 100, currency, locale) as string}
							// @ts-expect-error - $FixMe LinkList is js file
							valueClassName="link-list__status-unverified"
							StatusIcon={null}
							onClick={() => {}}
						/>
						<li>
							<LimitRemainingWrapper>
								<div>{translations.MOBILE_RESPONSIBLE_LIMIT_REMAINING}</div>
								<div>{currencyFormatter.format(remainingAmount, currency, locale)}</div>
							</LimitRemainingWrapper>
						</li>
					</ul>
				</LinkList>
			</LimitList>

			<InformationText>
				{meta?.lastCheck
					? `${translations.MOBILE_SPENDING_LIMIT_LATEST_CHECK_MESSAGE} ${meta.lastCheck}.${translations.MOBILE_SPENDING_LIMIT_LATEST_CHECK_MESSAGE_PART2}`
					: translations.MOBILE_SPENDING_LIMIT_NEVER_CHECK_MESSAGE}
			</InformationText>

			<ButtonContainer>
				{kreditzFirstEnabled ? (
					<>
						<ButtonRoundedPrimary data-at="spending-limit-modal" onClick={onVerifyWithBank}>
							{translations.MOBILE_VERIFY_WITH_BANK}
						</ButtonRoundedPrimary>
						<UploadOptionsContainer>
							<OrText>{translations.MOBILE_SPENDING_LIMIT_OR_TEXT}</OrText>
							<UploadLinkButton
								onClick={onUploadDocuments}
								className="spending-limit-rg-upload"
								data-at="responsible-gaming_welcompopup"
							>
								{translations.MOBILE_UPLOAD_DOCUMENTS}
							</UploadLinkButton>
						</UploadOptionsContainer>
					</>
				) : (
					<>
						<ButtonRoundedPrimary data-at="responsible-gaming_welcompopup" onClick={onUploadDocuments}>
							{translations.MOBILE_UPLOAD_DOCUMENTS}
						</ButtonRoundedPrimary>
						<UploadOptionsContainer>
							<OrText>{translations.MOBILE_SPENDING_LIMIT_OR_TEXT}</OrText>
							<UploadLinkButton
								onClick={onVerifyWithBank}
								className="spending-limit-rg-upload"
								data-at="spending-limit-modal"
							>
								{translations.MOBILE_VERIFY_WITH_BANK}
							</UploadLinkButton>
						</UploadOptionsContainer>
					</>
				)}
			</ButtonContainer>
		</LimitContainer>
	);
};

export default SpendingLimitContent;
